import type { Deployment as AppDeployment, Project } from '../../../types';
import type { ProjectMetadata, Deployment as ServiceDeployment } from '../../services/ProjectService';
import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    useAuth,
    useOrganization,
    useUser,
} from '@clerk/clerk-react';

import { CodeEditorRef } from '../canvas/CodeEditor';
import EmptyState from '../nav/EmptyState';
import LoadingState from '../ui/loading-state';
import ProjectCard from './ProjectCard';
import ProjectDetails from './ProjectDetails';
import { ProjectStatus } from '../../../types';
import ProjectsMenu from './ProjectsMenu';
import { RefreshCw } from 'lucide-react';
import { dbStorage } from '../../utils/dbStorage';
import { projectService } from '../../services/ProjectService';
import { pyodideService } from '../../services/PyodideService';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';

type Filter = 'all' | 'published' | 'drafts';

// Helper to check if two project arrays are equal
const areProjectsEqual = (a: ProjectMetadata[], b: ProjectMetadata[]) => {
    if (a.length !== b.length) return false;

    // Create a map of projects by ID for faster lookups
    const bMap = new Map(b.map(project => [project.id, project]));

    return a.every(projectA => {
        const projectB = bMap.get(projectA.id);
        // If project doesn't exist in B or updatedAt is different, they're not equal
        return projectB &&
            projectA.updatedAt === projectB.updatedAt &&
            projectA.isPublished === projectB.isPublished;
    });
};

const deploymentsFromMetadata = (data: ServiceDeployment): AppDeployment => ({
    projectSlug: data.projectSlug,
    build_url: data.build_url || '',
    createdAt: data.createdAt,
    deployment_id: data.deployment_id,
    githubUsername: data.githubUsername || '',
    isActive: data.isActive || false,
    message: data.message || '',
    orgId: data.orgId || null,
    service_url: data.service_url || '',
    status:
        data.status === 'completed'
            ? 'completed'
            : data.status === 'failed'
                ? 'failed'
                : 'in_progress',
    timestamp: data.timestamp || data.createdAt,
    updatedAt: data.updatedAt || data.createdAt,
    userId: data.userId || '',
    custom_domain: data.custom_domain,
});

/* ------------------------------ component ------------------------------ */

interface ProjectsManagerProps {
    onSelectProject?: (projectId: string) => void;
    onCreateNewProject: (type: string, prompt?: string) => void;
    codeEditorRef?: React.RefObject<CodeEditorRef>;
    onGeneratingChange?: (isGenerating: boolean) => void;
    initialPrompt?: string;
}

const ProjectsManager: React.FC<ProjectsManagerProps> = ({
    onSelectProject,
    onCreateNewProject,
    codeEditorRef,
    onGeneratingChange,
    initialPrompt
}) => {
    const { getToken } = useAuth();
    const { organization } = useOrganization();
    const { user } = useUser();
    const navigate = useNavigate();

    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [backgroundLoading, setBackgroundLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<Filter>('all');
    const [loadingTimedOut, setLoadingTimedOut] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Track whether the component is currently mounted. We intentionally start with
    // `false` and flip it to `true` inside a mount effect so that the flag is
    // reliable even when React 18 StrictMode intentionally mounts, unmounts and
    // remounts components during development.
    const isMountedRef = useRef(false);
    const initialLoadRef = useRef(false);
    const isLoadingFromApiRef = useRef(false);

    const handleDeleteProject = useCallback(
        async (projectId: string) => {
            try {
                setProjects(prev => prev.filter(p => p.id !== projectId));

                const pendingToast = toast.loading('Deleting project...');

                const token = await getToken();
                if (!token) throw new Error('Authentication token not available');

                projectService.setToken(token);
                await projectService.deleteProject(projectId);

                window.dispatchEvent(new CustomEvent('refreshProjects'));

                toast.success('Project deleted successfully', {
                    id: pendingToast
                });
            } catch (err) {
                console.error('Error deleting project:', err);

                toast.error(
                    `Failed to delete project: ${err instanceof Error ? err.message : 'Unknown error'}`
                );
            }
        },
        [getToken]
    );

    const handleDuplicateProject = useCallback(
        async (projectId: string) => {
            try {
                const pendingToast = toast.loading('Duplicating project...');

                const token = await getToken();
                if (!token) throw new Error('Authentication token not available');

                projectService.setToken(token);
                const duplicatedProject = await projectService.duplicateProject(projectId);

                const transformedProject = {
                    id: duplicatedProject.id,
                    name: duplicatedProject.name,
                    status: duplicatedProject.isPublished
                        ? ProjectStatus.PUBLISHED
                        : ProjectStatus.DRAFT,
                    repository: duplicatedProject.repository,
                    framework: duplicatedProject.framework,
                    createdAt: new Date(duplicatedProject.createdAt).toISOString(),
                    updatedAt: duplicatedProject.updatedAt
                        ? new Date(duplicatedProject.updatedAt).toISOString()
                        : new Date(duplicatedProject.createdAt).toISOString(),
                    deployments: (duplicatedProject.deployments || []).map(
                        deploymentsFromMetadata
                    ),
                    url: duplicatedProject.url || `/project/${duplicatedProject.id}`,
                    deploymentUrl: duplicatedProject.deploymentUrl,
                    branch: duplicatedProject.branch || 'main',
                    icon: duplicatedProject.icon || '📄',
                    ownerName:
                        duplicatedProject.ownerName || duplicatedProject.userId || 'You',
                    ownerImage: duplicatedProject.ownerImage || '',
                    isPublished: duplicatedProject.isPublished || false,
                    time: duplicatedProject.updatedAt
                        ? new Date(duplicatedProject.updatedAt).toISOString()
                        : new Date(duplicatedProject.createdAt).toISOString(),
                    onDelete: handleDeleteProject,
                    onDuplicate: handleDuplicateProject,
                    isQuickStart: duplicatedProject.isQuickStart || false,
                };
                setProjects(prev => [transformedProject, ...prev]);

                window.dispatchEvent(new CustomEvent('refreshProjects'));

                toast.success('Project duplicated successfully', {
                    id: pendingToast
                });
            } catch (err) {
                console.error('Error duplicating project:', err);

                toast.error(
                    `Failed to duplicate project: ${err instanceof Error ? err.message : 'Unknown error'}`
                );
            }
        },
        [getToken, handleDeleteProject]
    );

    // Transform API project data to UI Project object
    const transformProjectData = useCallback((projectData: ProjectMetadata): Project => {
        return {
            id: projectData.id,
            name: projectData.name,
            status: projectData.isPublished
                ? ProjectStatus.PUBLISHED
                : ProjectStatus.DRAFT,
            repository: projectData.repository,
            framework: projectData.framework,
            createdAt: new Date(projectData.createdAt).toISOString(),
            updatedAt: projectData.updatedAt
                ? new Date(projectData.updatedAt).toISOString()
                : new Date(projectData.createdAt).toISOString(),
            deployments: (projectData.deployments || []).map(
                deploymentsFromMetadata
            ),
            url: projectData.url || `/project/${projectData.id}`,
            deploymentUrl: projectData.deploymentUrl,
            branch: projectData.branch || 'main',
            icon: projectData.icon || '📄',
            ownerName:
                projectData.ownerName || projectData.userId || 'You',
            ownerImage: projectData.ownerImage || '',
            isPublished: projectData.isPublished || false,
            time: projectData.updatedAt
                ? new Date(projectData.updatedAt).toISOString()
                : new Date(projectData.createdAt).toISOString(),
            onDelete: handleDeleteProject,
            onDuplicate: handleDuplicateProject,
            isQuickStart: projectData.isQuickStart || false,
        };
    }, [handleDeleteProject, handleDuplicateProject]);

    // Load projects from IndexedDB cache (fast)
    const loadProjectsFromCache = useCallback(async () => {
        if (!user?.id || !isMountedRef.current) {
            console.log("Not mounted or user not found, skipping cache load", { user, isMountedRef });
            return null;
        }

        try {
            const orgId = organization?.id || null;
            console.log("Fetching projects from IndexedDB", { orgId });

            const projectsData = await dbStorage.getProjects(orgId);
            console.log(`Found ${projectsData.length} projects in cache`, { projectsData });

            if (projectsData.length > 0) {
                const projectsList = projectsData.map(project => transformProjectData(project));
                console.log(`Transformed ${projectsList.length} projects from cache`);
                return projectsList;
            } else {
                console.log("Project cache is empty, requesting refresh");
                return null;
            }
        } catch (err) {
            console.error("Error loading from cache:", err);
            return null;
        } finally {
            console.log("Ending fetchProjectsFromCache, setting loading to false");
        }
    }, [user, organization?.id, transformProjectData]);

    // Load projects from API (slow but up-to-date)
    const loadProjectsFromAPI = useCallback(async () => {
        if (!user || !isMountedRef.current) return null;

        // Prevent concurrent API calls
        if (isLoadingFromApiRef.current) {
            console.log("Already loading projects from API, skipping duplicate fetch");
            return null;
        }

        isLoadingFromApiRef.current = true;
        const loadingTimeout = setTimeout(() => {
            console.log("API loading timeout reached");
            isLoadingFromApiRef.current = false;
        }, 20000); // 20 second timeout for API call

        try {
            setBackgroundLoading(true);
            const token = await getToken();
            if (!token) throw new Error('Authentication token not available');

            projectService.setToken(token);
            projectService.setUserContext(user.id, organization?.id || null);

            console.log("Fetching latest projects from API");
            const projectsData = await projectService.listProjects();
            clearTimeout(loadingTimeout);

            if (isMountedRef.current) {
                const orgId = organization?.id || null;

                // Get current cache to compare
                const cachedProjects = await dbStorage.getProjects(orgId);

                // Only update if different
                if (!areProjectsEqual(projectsData, cachedProjects)) {
                    console.log("API projects differ from cache, updating...");
                    await dbStorage.storeProjects(projectsData, orgId);

                    const projectsList = projectsData.map(project => transformProjectData(project));
                    return projectsList;
                } else {
                    console.log("API projects match cache, no update needed");
                }
            }
        } catch (err) {
            console.error("Error fetching from API:", err);
            if (isMountedRef.current) {
                setError(err instanceof Error ? err.message : "Error loading projects");
            }
            return null;
        } finally {
            clearTimeout(loadingTimeout);
            if (isMountedRef.current) {
                setBackgroundLoading(false);
            }
            isLoadingFromApiRef.current = false;
        }
        return null;
    }, [user, organization?.id, getToken, transformProjectData]);

    // Initialize projects - fast path first, then update if needed
    const initializeProjects = useCallback(async () => {
        if (initialLoadRef.current || !user) return;
        initialLoadRef.current = true;

        setLoading(true);
        setError(null);

        try {
            // Step 1: Fast load from cache
            const cachedProjects = await loadProjectsFromCache();

            if (cachedProjects?.length) {
                // Immediately show cached projects
                setProjects(cachedProjects);
                setLoading(false);
                console.log("Rendered projects from cache, now fetching from API");

                // Step 2: In the background, fetch from API
                const apiProjects = await loadProjectsFromAPI();

                // Step 3: Update UI only if API returned different projects
                if (apiProjects && isMountedRef.current) {
                    setProjects(apiProjects);
                }
            } else {
                console.log("Cache empty or invalid, loading from API");
                // No projects in cache, start API call but don't wait for it to complete
                // to avoid UI getting stuck in loading state
                setProjects([]); // Initialize with empty array
                setLoading(false); // Stop loading immediately to show empty state

                // Load from API in background
                loadProjectsFromAPI().then(apiProjects => {
                    if (apiProjects && isMountedRef.current) {
                        setProjects(apiProjects);
                    }
                }).catch(err => {
                    console.error("Error loading projects from API:", err);
                    if (isMountedRef.current) {
                        setError(err instanceof Error ? err.message : "Error loading projects");
                    }
                });
            }
        } catch (err) {
            console.error("Error initializing projects:", err);
            if (isMountedRef.current) {
                setError(err instanceof Error ? err.message : "Error loading projects");
                setLoading(false);
                setProjects([]); // Ensure we have a fallback empty state
            }
        } finally {
            if (isMountedRef.current) {
                setInitialLoadComplete(true);
            }
        }
    }, [user, loadProjectsFromCache, loadProjectsFromAPI]);

    // Handler for projectsRefreshed event
    const handleProjectsRefreshed = useCallback(async () => {
        if (!initialLoadComplete) return;

        // Skip if already loading in background
        if (isLoadingFromApiRef.current) {
            console.log("Already refreshing projects, skipping duplicate refresh");
            return;
        }

        const apiProjects = await loadProjectsFromAPI();
        if (apiProjects && isMountedRef.current) {
            setProjects(apiProjects);
        }
    }, [loadProjectsFromAPI, initialLoadComplete]);

    // Handle user context setting
    useEffect(() => {
        if (user) projectService.setUserContext(user.id, organization?.id || null);
    }, [user?.id, organization?.id, user]);

    // Handle loading timeout
    useEffect(() => {
        if (loading) {
            timeoutRef.current = setTimeout(() => {
                if (isMountedRef.current && loading) {
                    console.log("Loading timeout reached, showing empty state");
                    setLoading(false);
                    setLoadingTimedOut(true);

                    // If no projects loaded yet, show empty state
                    if (projects.length === 0) {
                        setProjects([]);
                    }
                }
            }, 10000); // Increase timeout to 10 seconds for better API response chance

            return () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
            };
        } else {
            // Reset timeout state when loading stops naturally
            setLoadingTimedOut(false);
        }
    }, [loading, projects.length]);

    // Set up event listener for projectsRefreshed
    useEffect(() => {
        window.addEventListener('projectsRefreshed', handleProjectsRefreshed);
        return () => window.removeEventListener('projectsRefreshed', handleProjectsRefreshed);
    }, [handleProjectsRefreshed]);

    // ------------------------------------------------------------
    // Mount / Unmount tracking (must run first)
    // ------------------------------------------------------------
    // We declare this effect *before* any other effect that relies on the
    // `isMountedRef` flag so that the flag is set to `true` before those effects
    // fire on the initial mount.
    useEffect(() => {
        isMountedRef.current = true;
        // Reset the initial load flag so that the data fetching logic runs on
        // each real mount.
        initialLoadRef.current = false;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Initial load (runs after the mount tracking effect above)
    useEffect(() => {
        initializeProjects();
    }, [initializeProjects]);

    const filteredProjects = useMemo(() => {
        if (filter === 'all') return projects;
        return projects.filter(p =>
            filter === 'published'
                ? p.status === ProjectStatus.PUBLISHED
                : p.status === ProjectStatus.DRAFT
        );
    }, [projects, filter]);

    const handleFilterChange = useCallback(
        (val: Filter) => setFilter(val),
        []
    );

    const handleProjectSelect = useCallback(
        (project: Project) => {
            if (onSelectProject) {
                onSelectProject(project.id);
            } else {
                // For published projects, navigate to project details
                if (project.status === ProjectStatus.PUBLISHED) {
                    // Use TanStack Router to navigate to project details
                    navigate({
                        to: '/org/$orgSlug/project-details/$projectId',
                        params: {
                            orgSlug: organization?.slug || 'org',
                            projectId: project.id
                        }
                    });
                } else {
                    // For drafts, show in the editor instead
                    setSelectedProject(project);
                }
            }
        },
        [onSelectProject, organization?.slug, navigate]
    );

    const handleRefresh = useCallback(() => {
        // Only refresh if not already loading
        if (!isLoadingFromApiRef.current) {
            loadProjectsFromAPI();
        } else {
            console.log("Already refreshing projects, skipping manual refresh");
        }
    }, [loadProjectsFromAPI]);

    const handleGeneratingChange = useCallback((isGenerating: boolean) => {
        setIsGenerating(isGenerating);
        onGeneratingChange?.(isGenerating);
    }, [onGeneratingChange]);
    return (
        <div className="w-full p-6">
            {!selectedProject && (
                <ProjectsMenu
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    onCreateNewProject={onCreateNewProject}
                    initialPrompt={initialPrompt}
                />
            )}

            {error && (
                <div className="p-4 mb-6 bg-red-50 text-red-700 rounded-md border border-red-200">
                    <div className="flex justify-between items-center">
                        <div>{error}</div>
                        <button
                            onClick={handleRefresh}
                            className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                        >
                            <RefreshCw size={14} />
                            Retry
                        </button>
                    </div>
                </div>
            )}

            {loading && !loadingTimedOut ? (
                <LoadingState size="md" message="Loading projects…" />
            ) : selectedProject ? (
                <ProjectDetails
                    project={selectedProject}
                    onClose={() => setSelectedProject(null)}
                />
            ) : !filteredProjects.length && !error ? (
                loadingTimedOut ? (
                    <div className="p-4 mb-6 bg-blue-50 text-blue-700 rounded-md border border-blue-200">
                        <div className="flex justify-between items-center">
                            <div>
                                Taking a while to load projects. You can try refreshing.
                            </div>
                            <button
                                onClick={handleRefresh}
                                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                            >
                                <RefreshCw size={14} />
                                Refresh
                            </button>
                        </div>
                    </div>
                ) : (
                    <EmptyState onCreateNewProject={() => onCreateNewProject('blank')} />
                )
            ) : (
                <div className="max-w-7xl mx-auto">
                    {backgroundLoading && (
                        <div className="text-xs text-gray-500 mb-2 flex items-center">
                            <RefreshCw size={12} className="animate-spin mr-1" />
                            Syncing projects...
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {filteredProjects.map(project => (
                            <div
                                key={project.id}
                                onClick={e => {
                                    if (e.defaultPrevented) return;
                                    handleProjectSelect(project);
                                }}
                                className="relative transition transform hover:scale-[1.02] duration-200"
                            >
                                {!project.isPublished && (
                                    <div className="absolute top-2 left-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                                        Draft
                                    </div>
                                )}
                                <ProjectCard
                                    project={project}
                                    className="bg-white"
                                    onItemClick={(projectId, type) => {
                                        // Only use this handler for context menu clicks
                                        if (type === 'project' && onSelectProject) {
                                            onSelectProject(projectId);
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(ProjectsManager);